import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Star, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Panel, DarkBtn, ConfirmDelete, Field } from "@/components/admin/AdminUI";

export default function Reviews() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/reviews").then(({ data }) => setRows(data));
  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    const { id, created_at, ...body } = edit;
    try {
      if (id) await api.put(`/admin/reviews/${id}`, body);
      else await api.post("/admin/reviews", body);
      toast.success("Yorum kaydedildi");
      setEdit(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const remove = async (id) => {
    await api.delete(`/admin/reviews/${id}`);
    load();
  };

  return (
    <div data-testid="admin-reviews-page">
      <PageHeader title="Misafir Yorumları" desc="Google veya Instagram'dan aldığınız gerçek yorumları ekleyin. Aktif yorum varsa ana sayfada görünür.">
        <DarkBtn onClick={() => setEdit({ name: "", text: "", rating: 5, active: true })} data-testid="admin-review-add-btn"><Plus size={16} /> Yorum Ekle</DarkBtn>
      </PageHeader>
      <div className="grid md:grid-cols-2 gap-4">
        {!rows.length && <Panel className="p-10 text-center text-sm text-[#6B5E56] md:col-span-2">Henüz yorum eklenmedi.</Panel>}
        {rows.map((r) => (
          <Panel key={r.id} className={`p-5 ${r.active ? "" : "opacity-60"}`} data-testid={`admin-review-${r.id}`}>
            <div className="flex justify-between">
              <div className="flex gap-0.5 text-[#D4AF37]">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} size={14} fill="currentColor" />)}</div>
              <div className="flex gap-1">
                <button onClick={() => setEdit(r)} className="p-1.5 rounded-md hover:bg-[#F3EDE2]" aria-label="Düzenle" data-testid={`admin-review-edit-${r.id}`}><Pencil size={14} /></button>
                <ConfirmDelete onConfirm={() => remove(r.id)}>
                  <button className="p-1.5 rounded-md hover:bg-red-50 text-[#C84B31]" aria-label="Sil" data-testid={`admin-review-delete-${r.id}`}><Trash2 size={14} /></button>
                </ConfirmDelete>
              </div>
            </div>
            <p className="mt-3 font-serif-ym text-lg">"{r.text}"</p>
            <p className="mt-2 text-sm text-[#6B5E56]">— {r.name}</p>
          </Panel>
        ))}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-serif-ym text-2xl">{edit?.id ? "Yorumu Düzenle" : "Yeni Yorum"}</DialogTitle></DialogHeader>
          {edit && (
            <form onSubmit={save} className="space-y-4" data-testid="review-form">
              <Field label="Misafir adı *"><Input required value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="review-name-input" /></Field>
              <Field label="Yorum *"><Textarea required rows={4} value={edit.text} onChange={(e) => setEdit({ ...edit, text: e.target.value })} data-testid="review-text-input" /></Field>
              <Field label="Puan">
                <div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => (
                  <button type="button" key={n} onClick={() => setEdit({ ...edit, rating: n })} className="text-[#D4AF37]" data-testid={`review-rating-${n}`}><Star size={22} fill={n <= edit.rating ? "currentColor" : "none"} /></button>
                ))}</div>
              </Field>
              <label className="flex items-center gap-2 text-sm"><Switch checked={edit.active} onCheckedChange={(v) => setEdit({ ...edit, active: v })} data-testid="review-active-switch" /> Sitede göster</label>
              <DarkBtn type="submit" className="w-full" data-testid="review-save-btn">Kaydet</DarkBtn>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
